import * as z from 'zod';

import { AttachmentSchema } from './common/attachment';

export const LightSourceSchema = z.enum(
  [
    'incandescent',
    'fluorescent',
    'compact_fluorescent',
    'halogen',
    'metal_halide',
    'high_pressure_sodium',
    'led',
  ],
  { error: 'Light Source is required' },
);
export type LightSource = z.infer<typeof LightSourceSchema>;

export const LampTypeSchema = z.enum(
  [
    '2pin_pl',
    '4pin_pl',
    't5_4ft',
    't8_4ft',
    't12_4ft',
    't8_1ft',
    't8_18in',
    't8_2ft',
    't8_3ft',
    't8_8ft',
    't8_u',
    't12_8ft',
    't12_u',
    'a15',
    'a19',
    'a21',
    'br30',
    'br38',
    'br40',
    'candle',
    'g25',
    'g40',
    'g100',
    'par20',
    'par30',
    'par38',
    'r20',
    'r30',
    'r40',
    'mr16',
    't3',
    'x',
    'retrofit_kit',
    'exit',
  ],
  { error: 'Lamp Type is required' },
);
export type LampType = z.infer<typeof LampTypeSchema>;

export const LampBaseSchema = z.enum([
  'b4_5',
  'b15',
  'e12',
  'e26',
  'e39',
  'g23',
  'g24d',
  'g24q',
  'gu5_3',
  'gu10',
  'gu24',
  'gx23',
  'gx24q',
  'r7s',
  'med_bipin',
  'single_pin',
  'x',
  'unknown',
]);
export type LampBase = z.infer<typeof LampBaseSchema>;

export const FixtureTypeSchema = z.enum(
  [
    'strip',
    'troffer',
    'pot_light',
    'keyless',
    'high_bay',
    'low_bay',
    'marine',
    'flood',
    'panel',
    'track',
    'sconce',
    'under_cabinet',
    'cylinder',
    'wall_pack',
    'pole_light',
    'unknown',
  ],
  { error: 'Fixture Type is required' },
);
export type FixtureType = z.infer<typeof FixtureTypeSchema>;

export const AttributeSchema = z.enum([
  'cage',
  'open',
  'wrap',
  'circular',
  'dome',
  'globe',
  'linear',
  'square',
  'full_cutoff',
  'gimble',
  'half_cutoff',
  'horizontal_bulb',
  'up_down',
  'vertical_bulb',
  'architectural',
  'dimmable',
  'valence',
  'vanity',
  'metric',
  'shoebox',
  'cobra',
  'yoke',
  'knuckle',
]);
export type Attribute = z.infer<typeof AttributeSchema>;

export const ColorTemperatureSchema = z.enum([
  '2400k',
  '2700k',
  '3000k',
  '3500k',
  '4000k',
  '4500k',
  '5000k',
  '6000k',
]);
export type ColorTemperature = z.infer<typeof ColorTemperatureSchema>;

export const AuditLightingTypeFormEntrySchema = z.object({
  id: z.string(),
  quantity: z.coerce.number().min(1).max(12).optional(),
  lightSource: LightSourceSchema,
  lampType: LampTypeSchema,
  lampBase: LampBaseSchema.optional(),
  lampWattage: z.coerce.number().optional(),
  fixtureType: FixtureTypeSchema,
  fixtureSize: z.string().optional(),
  fixtureMount: z.string().optional(),
  attributes: z.array(AttributeSchema).optional(),
  colorTemperature: ColorTemperatureSchema.optional(),
  ballastFactor: z.coerce.number().optional(),
  fixtureWattage: z.coerce.number().optional(),
  luminaireName: z.string().optional(),
  comments: z.string().optional(),
  attachments: z.array(AttachmentSchema).optional(),
});
export type AuditLightingTypeFormEntry = z.infer<
  typeof AuditLightingTypeFormEntrySchema
>;

export const AuditLightingTypeFormSchema = z.object({
  type: z.literal('lighting-type'),

  entries: z.array(AuditLightingTypeFormEntrySchema),
});
export type AuditLightingTypeForm = z.infer<typeof AuditLightingTypeFormSchema>;
